"use client"

import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import SearchableDropdown from "../common/searchable-dropdown/SearchableDropdown"
import AccountPropertyForm from "../common/account-property-form/AccountPropertyForm"
import { searchBrandHotels } from "../../store/slices/hotelsSlice"
import { searchSubBrandHotels, clearSubBrands } from "../../store/slices/organizationSlice"
import { createClient, clearCreateClient } from "../../store/slices/createClientSlice"
import { startOnboarding, clearOnboarding } from "../../store/slices/onboardingSlice"
import { fetchUserProfile } from "../../store/slices/userSlice"
import { fetchEventsByClientId } from "../../store/slices/eventsSlice"
import {
  mapBrandSearchToDropdownOption,
  mapPropertySearchToDropdownOption,
  coerceHotelSearchTextField,
  safeHotelSearchDisplayText,
} from "../../lib/utils/hotelSearchMappers"
import "./SignupForm.css"


const hasAtLeastOneAlphabet = (value) => {
  return /[a-zA-Z]/.test(value)
}


const WEBSITE_REGEX = /^https:\/\/(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?(\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=]*)?$/

const isValidWebsite = (url) => {
  if (!url) return false
  return WEBSITE_REGEX.test(url.trim())
}


const SignupStepTwo = ({ onComplete, stepOneData }) => {
  const dispatch = useDispatch()
  // const { hotels: brands, loading: brandsLoading } = useSelector((state) => state.hotels)
  // const { hotels, loading: hotelsLoading } = useSelector((state) => state.hotels)
  const { brands, loadingBrands } = useSelector((state) => state.hotels)

  // SUB-BRAND / PROPERTY SEARCH (comes from organizationSlice)
  const { subBrands, loadingSubBrands } = useSelector((state) => state.organization)



  const [showSetupPopup, setShowSetupPopup] = useState(false)
  const [progress, setProgress] = useState(0)
  const [progressText, setProgressText] = useState({
    title: "",
    message: "",
  })


  // HOTELS (if you still use searchHotels)
  const { hotels, loadingHotels } = useSelector((state) => state.hotels)
  const {
    loading: createClientLoading,
    success: clientCreated,
    error: createClientError,
    client: createdClient,
  } = useSelector((state) => state.createClient)
  const {
    loading: onboardingLoading,
    success: onboardingSuccess,
    error: onboardingError,
  } = useSelector((state) => state.onboarding)

  const [validationErrors, setValidationErrors] = useState({})
  const orgIdRef = useRef(null)
  const onboardingStartedRef = useRef(false)
  const progressIntervalRef = useRef(null)
  const pendingCompleteRef = useRef(null)

  const clearProgressInterval = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
      progressIntervalRef.current = null
    }
  }, [])

  const [formData, setFormData] = useState(() => {
    try {
      const saved = typeof window !== "undefined" && sessionStorage.getItem("signup_step_two_data")
      if (saved) {
        const parsed = JSON.parse(saved)
        return {
          organization: "",
          organizationId: null,
          websiteUrl: "",
          property: "",
          propertyId: null,
          brand: "",
          brandId: null,
          subBrand: "",
          subBrandId: null,
          country: "",
          countryId: null,
          city: "",
          cityId: null,
          socialNetworkTypeId: null,
          socialHandle: "",
          ...parsed,
          organization: coerceHotelSearchTextField(parsed?.organization),
          property: coerceHotelSearchTextField(parsed?.property, { preferProperty: true }),
          brand: coerceHotelSearchTextField(parsed?.brand),
          subBrand: coerceHotelSearchTextField(parsed?.subBrand, { preferProperty: true }),
        }
      }
    } catch (_) {}
    return {
      organization: "",
      organizationId: null,
      websiteUrl: "",
      property: "",
      propertyId: null,
      brand: "",
      brandId: null,
      subBrand: "",
      subBrandId: null,
      country: "",
      countryId: null,
      city: "",
      cityId: null,
      socialNetworkTypeId: null,
      socialHandle: "",
    }
  })

  const organizationDisplay = safeHotelSearchDisplayText(formData.organization)
  const propertyDisplay = safeHotelSearchDisplayText(formData.property, { preferProperty: true })

  const brandOptions = Array.isArray(brands)
    ? brands.map(mapBrandSearchToDropdownOption).filter((opt) => opt.label)
    : []

  const propertyOptions = Array.isArray(subBrands)
    ? subBrands.map(mapPropertySearchToDropdownOption).filter((opt) => opt.label)
    : []

  //   const progressSteps = {
  //   25: {
  //     title: "Analyzing Your Property",
  //     message: `Discovering ${propertyOptions[0]?.value }`,

  //   },
  //   50: {
  //     title: "Fetching Social Channels",
  //     message: "Finding Your Platforms",
  //   },
  //   75: {
  //     title: "Identifying Target Audience",
  //     message: "Understanding Your Guests",
  //   },
  //   100: {
  //     title: "Extracting Content Pillars",
  //     message: "Content Strategy Foundation",


  //   },
  // }
  const progressSteps = useMemo(
    () => ({
      25: {
        title: "Analyzing Your Property",
        message: `Discovering ${propertyDisplay || ""}`,
        description: `Scanning your hotel's online presence and location data for ${formData.city || ""}, ${formData.country || ""}`,
      },
      50: {
        title: "Fetching Social Channels",
        message: "Finding Your Platforms",
        description:
          "Detecting existing social media accounts and analyzing current content strategies.",
      },
      75: {
        title: "Identifying Target Audience",
        message: "Understanding Your Guests",
        description:
          "Analyzing guest demographics and booking patterns to define your ideal audience segments.",
      },
      95: {
        title: "Extracting Content Pillars",
        message: "Content Strategy Foundation",
        description:
          "Identifying key content themes based on your property type, location, and guest preferences.",
      },
      100: {
        title: "Information Gathered!",
        description:
          "We've successfully collected your social channels, target audience data, and content pillars. Ready for setup!",
      },
    }),
    [propertyDisplay, formData.city, formData.country],
  )

  const runGatheringProgress = useCallback(() => {
    clearProgressInterval()
    const steps = [25, 50, 75, 95]
    let index = 0
    setProgress(steps[0])
    setProgressText({ ...progressSteps[steps[0]] })
    progressIntervalRef.current = setInterval(() => {
      index += 1
      if (index < steps.length) {
        setProgress(steps[index])
        setProgressText({ ...progressSteps[steps[index]] })
      } else {
        clearProgressInterval()
      }
    }, 2000)
  }, [clearProgressInterval, progressSteps])

  useEffect(() => {
    if (formData.organizationId) {
      setFormData((prev) => ({
        ...prev,
        property: "",
        propertyId: null,
      }))
    }
  }, [formData.organizationId])

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await dispatch(fetchUserProfile({ force: true })).unwrap()
      } catch (err) {
        console.error("Initial fetch error:", err)
      }
    }

    fetchInitialData()
  }, [dispatch])

  // Persist step 2 form so refresh on client page keeps user on step 2 with data
  useEffect(() => {
    sessionStorage.setItem("signup_step_two_data", JSON.stringify(formData))
  }, [formData])

  // Repair legacy session rows that stored full API objects in organization/property
  useEffect(() => {
    setFormData((prev) => {
      const organization = coerceHotelSearchTextField(prev.organization)
      const property = coerceHotelSearchTextField(prev.property, { preferProperty: true })
      if (organization === prev.organization && property === prev.property) return prev
      return { ...prev, organization, property }
    })
  }, [])




  useEffect(() => () => clearProgressInterval(), [clearProgressInterval])

  useEffect(() => {
    if (!clientCreated || !createdClient || onboardingStartedRef.current) return

    const clientId = createdClient.id || createdClient.clientId
    if (!clientId) return

    onboardingStartedRef.current = true
    pendingCompleteRef.current = { ...stepOneData, ...formData, clientId }

    localStorage.setItem("signup_session", "true")
    localStorage.setItem("signup_email", stepOneData.businessEmail)
    localStorage.setItem("onboarding_client_id", String(clientId))

    runGatheringProgress()

    dispatch(startOnboarding({ clientId }))
    dispatch(fetchEventsByClientId({ clientId, date: new Date() }))
    dispatch(clearCreateClient())
  }, [
    clientCreated,
    createdClient,
    dispatch,
    formData,
    stepOneData,
    runGatheringProgress,
  ])

  useEffect(() => {
    if (!onboardingSuccess || !pendingCompleteRef.current) return

    clearProgressInterval()
    setProgress(100)
    setProgressText({ ...progressSteps[100] })

    const doneTimer = setTimeout(() => {
      const payload = pendingCompleteRef.current
      pendingCompleteRef.current = null
      if (payload) onComplete(payload)
    }, 1200)

    return () => clearTimeout(doneTimer)
  }, [onboardingSuccess, onComplete, clearProgressInterval, progressSteps])

  useEffect(() => {
    if (!onboardingError || !showSetupPopup) return
    clearProgressInterval()
    setProgress(95)
    setProgressText({
      title: "Setup could not complete",
      message: "",
      description:
        typeof onboardingError === "string"
          ? onboardingError
          : "Something went wrong while gathering your hotel information. Please try again.",
    })
  }, [onboardingError, showSetupPopup, clearProgressInterval])

  useEffect(() => {
    if (!createClientError || !showSetupPopup) return
    clearProgressInterval()
    onboardingStartedRef.current = false
    pendingCompleteRef.current = null
    setShowSetupPopup(false)
  }, [createClientError, showSetupPopup, clearProgressInterval])

  const handleSearch = useCallback(
    (name, query) => {

      const trimmedQuery = query?.trim() || "";

      //  Organization search
      if (name === "organization") {

        return dispatch(
          searchBrandHotels({
            query: trimmedQuery,
            page: 0,
            size: 300,
          })
        );

      }

      //  Property search
      if (name === "property") {

        const brandId = orgIdRef.current || formData.organizationId;

        if (!brandId) {
          dispatch(clearSubBrands());
          return;
        }

        return dispatch(
          searchSubBrandHotels({
            brandId,
            query: trimmedQuery,
            page: 0,
            size: 100,
          })
        );
      }

    },
    [dispatch],
  )

  const handleDropdownChange = useCallback((fieldName, value, item) => {
    if (fieldName === "websiteUrl") {
      setValidationErrors((prev) => ({ ...prev, websiteUrl: "" }))
      setFormData((prev) => ({ ...prev, websiteUrl: value }))
      return
    }

    if (fieldName === "socialNetwork") {
      setValidationErrors((prev) => ({ ...prev, socialNetwork: "" }))
      setFormData((prev) => ({ ...prev, socialNetworkTypeId: item?.id ?? null }))
      return
    }

    if (fieldName === "socialHandle") {
      setValidationErrors((prev) => ({ ...prev, socialHandle: "" }))
      setFormData((prev) => ({ ...prev, socialHandle: value }))
      return
    }

    if (fieldName === "organization") {
      const brandId = Number(item?.id) || null

      orgIdRef.current = brandId
      dispatch(clearSubBrands())

      setValidationErrors((prev) => ({
        ...prev,
        organization:
          value && !hasAtLeastOneAlphabet(safeHotelSearchDisplayText(value))
            ? "Organization name must contain at least one letter"
            : "",
      }))

      setFormData((prev) => ({
        ...prev,
        organization: safeHotelSearchDisplayText(value),
        organizationId: brandId,
        property: "",
        propertyId: null,
      }))

      if (brandId) {
        dispatch(
          searchSubBrandHotels({
            brandId,
            query: "",
            page: 0,
            size: 100,
          }),
        )
      }

      return
    }

    if (fieldName === "property") {
      setValidationErrors((prev) => ({
        ...prev,
        property:
          value && !hasAtLeastOneAlphabet(safeHotelSearchDisplayText(value, { preferProperty: true }))
            ? "Property name must contain at least one letter"
            : "",
      }))

      setFormData((prev) => ({
        ...prev,
        property: safeHotelSearchDisplayText(value, { preferProperty: true }),
        propertyId: item?.id ?? null,
      }))

      return
    }

    if (fieldName === "brand") {
      setValidationErrors((prev) => ({ ...prev, brand: "", subBrand: "" }))
      setFormData((prev) => ({
        ...prev,
        brand: safeHotelSearchDisplayText(value),
        brandId: item?.id ?? null,
        subBrand: "",
        subBrandId: null,
      }))
      return
    }

    if (fieldName === "subBrand") {
      setValidationErrors((prev) => ({ ...prev, subBrand: "" }))
      setFormData((prev) => ({
        ...prev,
        subBrand: safeHotelSearchDisplayText(value, { preferProperty: true }),
        subBrandId: item?.id ?? null,
      }))
      return
    }

    if (fieldName === "country") {
      setFormData((prev) => ({
        ...prev,
        country: value,
        countryId: item?.id ?? null,
        city: "",
        cityId: null,
      }))

      return
    }

    if (fieldName === "city") {
      setFormData((prev) => ({
        ...prev,
        city: value,
        cityId: item?.id ?? null,
      }))
    }
  }, [dispatch])


  const handleSubmit = (e) => {
    e.preventDefault()
    if (showSetupPopup || createClientLoading || onboardingLoading) return

    const errors = {}
    let hasError = false

    if (!organizationDisplay.trim()) {
      errors.organization = "Organization is required"
      hasError = true
    } else if (!hasAtLeastOneAlphabet(organizationDisplay)) {
      errors.organization = "Organization name must contain at least one letter"
      hasError = true
    }

    if (!propertyDisplay.trim()) {
      errors.property = "Property type is required"
      hasError = true
    }

    if (formData.websiteUrl?.trim() && !isValidWebsite(formData.websiteUrl)) {
      errors.websiteUrl = "Please enter a valid website URL"
      hasError = true
    }

    if (!formData.country || !formData.countryId) {
      errors.country = "Country is required"
      hasError = true
    }

    if (!formData.city || !formData.cityId) {
      errors.city = "City/State is required"
      hasError = true
    }

    if (formData.socialHandle && !formData.socialNetworkTypeId) {
      errors.socialNetwork = "Select a social network"
      hasError = true
    }

    setValidationErrors(errors)

    if (hasError) {
      const firstErrorField = Object.keys(errors)[0]
      const element = document.getElementById(firstErrorField)
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" })
      }
      return
    }

    const clientData = {
      organization: organizationDisplay,
      property: propertyDisplay,
      websiteUrl: formData.websiteUrl,
      cityId: formData.cityId,
      socialNetworkTypeId: formData.socialNetworkTypeId,
      socialHandle: formData.socialHandle,
      brandId: formData.brandId,
      subBrandId: formData.subBrandId,
    }

    setShowSetupPopup(true)
    setProgress(10)
    setProgressText({
      title: "Creating your property",
      message: "Setting up your workspace",
      description: "Saving your property details…",
    })

    dispatch(createClient(clientData))
  }

  const handleSkipOnboarding = () => {
    onComplete({ ...stepOneData, ...formData, skipped: true })
  }

  const isFormComplete =
    propertyDisplay.trim() &&
    formData.countryId &&
    formData.cityId

  // Only warn when user can submit (required fields filled) but optional website/social is missing
  const showOptionalWarning =
    isFormComplete &&
    (!formData.websiteUrl?.trim() || !formData.socialNetworkTypeId || !formData.socialHandle?.trim())


  return (
    <>
      <div className="signup-page" role="main" aria-label="Signup Form - Step 2">
        <div className="signup-container">

          <div>
            <div className="formHeader">
              <h2>Tell us about your property</h2>
              <p>
                We'll use this to personalize your workspace and tailor the onboarding to your property.
              </p>
            </div>
            <div className="signup-box">

              <form onSubmit={handleSubmit}>
                <div>
                  <h3 className="property-heading">Property</h3>
                  <div className="form-field">
                    <SearchableDropdown
                      id="organization"
                      name="organization"
                      label="Organization Name"
                      tooltip="Enter your hotel group or brand name. If you're a standalone property with no parent brand, simply enter your property name here."

                      value={organizationDisplay}
                      onChange={(text, option) => handleDropdownChange("organization", text, option)}
                      onSearch={(query) => handleSearch("organization", query)}
                      options={brandOptions.length > 0 ? brandOptions : brands}
                      normalizeHotelSearchOptions
                      placeholder="Enter your organization/brand name"
                      //required
                      loading={loadingBrands}
                      error={validationErrors.organization}
                    />
                  </div>
                  <div className="form-field">
                    <SearchableDropdown
                      id="property"
                      name="property"
                      label="Property Name"
                      tooltip="Enter the name of your hotel. If you're a standalone property, repeat the same name you entered for Organization Name."
                      value={propertyDisplay}
                      onChange={(text, option) => handleDropdownChange("property", text, option)}
                      onSearch={(query) => handleSearch("property", query)}
                      options={propertyOptions.length > 0 ? propertyOptions : subBrands}
                      normalizeHotelSearchOptions
                      preferPropertyOption
                      placeholder="Enter your hotel/business name"
                      loading={loadingSubBrands}
                      error={validationErrors.property}
                    />

                  </div>
                  <AccountPropertyForm
                    idPrefix="signup"
                    websiteUrl={formData.websiteUrl}
                    brandId={formData.brandId}
                    brandText={formData.brand || ""}
                    subBrandId={formData.subBrandId}
                    subBrandText={formData.subBrand || ""}
                    socialNetworkTypeId={formData.socialNetworkTypeId}
                    socialHandle={formData.socialHandle}
                    countryId={formData.countryId}
                    countryText={formData.country}
                    cityId={formData.cityId}
                    cityText={formData.city}
                    onChange={handleDropdownChange}
                    errors={validationErrors}
                  />
                </div>

                {createClientError && <p className="error-text">{createClientError}</p>}

                {showOptionalWarning && (
                  <div className="signup-warning-box" role="alert">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M10 6V10M10 14H10.01M9 2L1 16H19L11 2H9Z" stroke="#DC7D1B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <p>Connect a social channel and website URL to unlock AI features. You can continue, but the functionality will be limited.</p>
                  </div>
                )}

                <div style={{ display: "flex", gap: "12px", marginTop: "32px", justifyContent: "end" }}>
                  {/* <button
                type="button"
                onClick={handleSkipOnboarding}
                className="signup-btn"
                style={{
                  background: "#fff",
                  color: "#455A64",
                  border: "1px solid #CFD8DC",                  
                }}
              >
                Skip Onboarding
              </button> */}
                  <button
                    type="submit"
                    className={`signup-btn ${isFormComplete ? "active" : ""}`}
                    disabled={!isFormComplete}
                    aria-busy={showSetupPopup || createClientLoading || onboardingLoading}
                  >
                    Continue to Setup
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {showSetupPopup && (
        <div className="setup-overlay">
          <div className="setup-modal">
            <div className="setupHeader">
              {/* SUCCESS ICON */}
              <div className="setup-icon-wrapper">
                <img src="/assets/icons/common/lightbg_checkmark.svg" />
              </div>

              {/* TITLE */}
              <h3 className="setup-title">
                {progress >= 25 && !createClientLoading
                  ? "Account Created Successfully!"
                  : "Setting up your account"}
              </h3>

              <p className="setup-subtitle">
                Gathering information for{" "}
                <strong>{propertyDisplay}</strong>
              </p>
            </div>
            {/* <div className="setup-divider" /> */}

            {/* STEP CARD */}
            <div className="card_body">
              <div className="setup-step-card ">
                {progress !== 100 && (
                  <div className="setup-step-icon">
                    <img src="/assets/icons/common/analyse.svg" />
                  </div>)}

                <div className="setup-step-content">
                  <h4 className={`${progress == 100 ? "text-center-100-setup" : ""}`}>
                    {progressText?.title}</h4>

                  {progress !== 100 && progressText?.message && (
                    <span className="setup-step-sub">
                      {progressText.message}
                    </span>
                  )}

                  {/* <p>
                    We are automatically configuring your workspace based
                    on your property details.
                  </p> */}
                  <p className={`${progress == 100 ? "text-center-100-setup" : ""}`}>{progressText?.description}</p>
                </div>
              </div>

              <div className="setup-progress-header">
                <span>
                  {progress < 25
                    ? "Creating your property"
                    : progress <= 95
                      ? "Gathering your hotel information"
                      : "Complete"}
                </span>

                <span className="setup-progress-percent">
                  {progress}%
                </span>
              </div>

              <div className="setup-progress-bar">
                <div
                  className="setup-progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              {progress === 100 && onboardingSuccess && (
                <div className="setup-final-message">
                  Taking you to onboarding steps...
                </div>
              )}
              {onboardingError && (
                <div className="setup-final-message" style={{ color: "#b91c1c" }}>
                  <button
                    type="button"
                    className="signup-btn active"
                    style={{ marginTop: "12px" }}
                    onClick={() => {
                      clearProgressInterval()
                      onboardingStartedRef.current = false
                      pendingCompleteRef.current = null
                      setShowSetupPopup(false)
                      dispatch(clearOnboarding())
                    }}
                  >
                    Close and try again
                  </button>
                </div>
              )}

            </div>

          </div>
        </div>
      )}


    </>

  )

}

export default SignupStepTwo
